import { PdfLayout } from '../PdfLayout';
import { Radar, Search, Fingerprint, Cpu, PlugZap } from 'lucide-react';

const benefits = [
  {
    icon: Radar,
    title: 'Auto-Discovery',
    text: 'Scan every network the appliance is on, or the subnets you name. ARP discovery also finds devices still on a factory-default address.',
  },
  {
    icon: Search,
    title: 'Port Probing',
    text: 'Checks every host for the ports industrial protocols use — Modbus, EtherNet/IP, S7, OPC-UA, FOCAS, MQTT, and more — plus any ports you add.',
  },
  {
    icon: Fingerprint,
    title: 'Protocol Fingerprinting',
    text: 'Asks MTConnect agents, Modbus devices, and EtherNet/IP controllers to identify themselves, and scores each result with a confidence level.',
  },
  {
    icon: Cpu,
    title: 'Device Identification',
    text: 'Shows the network card\'s maker for every device, reads product, vendor, firmware and serial from EtherNet/IP devices, and the model, firmware and running program from Rockwell Logix controllers.',
  },
  {
    icon: PlugZap,
    title: 'One-Click Adopt',
    text: 'Adopt a discovered device and it is created with the right driver and port, connected, and ready for you to pick its tags.',
  },
];

export function NetworkScanner() {
  return (
    <PdfLayout
      category="Discovery"
      headline="Discover Every Device with the"
      headlineHighlight="Network Scanner"
      description="Point Scada.Ink at your network and watch it map your factory floor. No spreadsheets of addresses, no IP hunting — devices appear as they are found, ready to adopt."
      accentColor="#06b6d4"
      benefits={benefits}
    >
      <ScannerMockup />
    </PdfLayout>
  );
}

function ScannerMockup() {
  const devices = [
    { ip: '192.168.1.10', name: 'Haas VF-2', proto: 'MTConnect', confidence: 100 },
    { ip: '192.168.1.11', name: 'Mazak QT-250', proto: 'MTConnect', confidence: 100 },
    { ip: '192.168.1.20', name: 'Siemens S7-1200', proto: 'Siemens S7', confidence: 70 },
    { ip: '192.168.1.21', name: 'AB CompactLogix', proto: 'EtherNet/IP', confidence: 100 },
    { ip: '192.168.1.30', name: 'Fanuc 0i-TF', proto: 'FOCAS', confidence: 70 },
    { ip: '192.168.1.40', name: 'Temp Sensor Bank', proto: 'Modbus TCP', confidence: 80 },
  ];

  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Network Scanner</span>
      </div>

      {/* Scan progress */}
      <div className="glass-card p-2 mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-medium text-text-primary">Scanning 192.168.1.0/24</span>
          <span className="text-[8px] text-brand-accent font-medium">87%</span>
        </div>
        <div className="w-full h-1.5 bg-glass-border rounded-full overflow-hidden">
          <div className="h-full bg-brand-accent rounded-full" style={{ width: '87%' }} />
        </div>
      </div>

      {/* Results table */}
      <div className="glass-card overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-x-2 px-2 py-1.5 bg-ink-muted text-[7px] font-medium text-text-muted border-b border-glass-border">
          <span>Device</span>
          <span>Protocol</span>
          <span>Conf.</span>
        </div>
        {devices.map((d) => (
          <div
            key={d.ip}
            className="grid grid-cols-[1fr_1fr_auto] gap-x-2 px-2 py-1.5 text-[7px] border-b border-glass-border last:border-0"
          >
            <div>
              <div className="font-medium text-text-primary truncate">{d.name}</div>
              <div className="text-text-muted">{d.ip}</div>
            </div>
            <span className="text-text-secondary self-center">{d.proto}</span>
            <div className="self-center flex items-center gap-1">
              <div className={`w-1.5 h-1.5 rounded-full ${d.confidence >= 80 ? 'bg-status-running' : 'bg-status-idle'}`} />
              <span className="text-text-muted">{d.confidence}%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">6 devices found</span>
        <span className="text-[8px] text-brand-accent font-medium">● Scanning</span>
      </div>
    </div>
  );
}
